import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
    collection,
    doc,
    getDoc,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const ADMIN_ROLES = new Set(['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc']);
const RESOLVED_REPORT_STATUSES = new Set([
    'resuelta',
    'resuelto',
    'cerrada',
    'cerrado',
    'completada',
    'completado',
    'finalizada',
    'finalizado',
]);

const DEFAULT_VALUE = {
    pendingVotes: 0,
    unreadNotices: 0,
    totalPending: 0,
    pendingReports: 0,
    residentPendingReports: 0,
    ballotsWithVotes: 0,
    totalVotesCast: 0,
    pendingSolicitudes: 0,
    pendingSolicitudSummary: {
        mudanza: 0,
        construccion: 0,
        remodelacion: 0,
        otros: 0,
    },
    pendingDeliveries: 0,
    recentDeliveries: [],
    adminAlertTotal: 0,
    pendingFinancialAlerts: 0,
    financialAlerts: [],
    loading: false,
    isResident: false,
    isAdminRole: false,
    role: '',
};

const ResidentPendingContext = createContext(DEFAULT_VALUE);

export const ResidentPendingProvider = ({ children }) => {
    const { userData, currentUser } = useAuth();
    const normalizedRole = String(userData?.rol || '').toLowerCase();
    const isResident = normalizedRole === 'user' || normalizedRole === 'residente';
    const isAdminRole = ADMIN_ROLES.has(normalizedRole);
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const uid = userData?.uid || currentUser?.uid || null;

    const [pendingVotes, setPendingVotes] = useState(0);
    const [unreadNotices, setUnreadNotices] = useState(0);
    const [pendingReports, setPendingReports] = useState(0);
    const [residentPendingReports, setResidentPendingReports] = useState(0);
    const [ballotsWithVotes, setBallotsWithVotes] = useState(0);
    const [totalVotesCast, setTotalVotesCast] = useState(0);
    const [pendingSolicitudes, setPendingSolicitudes] = useState(0);
    const [pendingSolicitudSummary, setPendingSolicitudSummary] = useState({
        mudanza: 0,
        construccion: 0,
        remodelacion: 0,
        otros: 0,
    });
    const [pendingDeliveries, setPendingDeliveries] = useState(0);
    const [recentDeliveries, setRecentDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingFinancialAlerts, setPendingFinancialAlerts] = useState(0);
    const [financialAlerts, setFinancialAlerts] = useState([]);

    const activeUnsubscribesRef = useRef([]);

    // Escuchar evento global de cierre de sesión para cancelar listeners inmediatamente
    useEffect(() => {
        const handleLogoutCleanup = () => {
            console.log('ResidentPendingProvider: cancelando listeners activos por cierre de sesión...');
            activeUnsubscribesRef.current.forEach((unsub) => {
                try {
                    if (typeof unsub === 'function') unsub();
                } catch (e) {
                    console.warn('ResidentPendingProvider: error cancelando listener:', e);
                }
            });
            activeUnsubscribesRef.current = [];
        };

        window.addEventListener('app:logout_cleanup', handleLogoutCleanup);
        return () => window.removeEventListener('app:logout_cleanup', handleLogoutCleanup);
    }, []);

    useEffect(() => {
        let cancelled = false;
        let unsubscribeResidentVotes = () => undefined;
        let unsubscribeResidentNotices = () => undefined;
        let unsubscribeResidentDeliveries = () => undefined;
        let unsubscribeResidentReports = () => undefined;
        let unsubscribeReports = () => undefined;
        let unsubscribeAdminVotes = () => undefined;
        let unsubscribeSolicitudes = () => undefined;
        let unsubscribeFinancialAlerts = () => undefined;

        activeUnsubscribesRef.current = [
            () => { try { unsubscribeResidentVotes(); } catch (e) {} },
            () => { try { unsubscribeResidentNotices(); } catch (e) {} },
            () => { try { unsubscribeResidentDeliveries(); } catch (e) {} },
            () => { try { unsubscribeResidentReports(); } catch (e) {} },
            () => { try { unsubscribeReports(); } catch (e) {} },
            () => { try { unsubscribeAdminVotes(); } catch (e) {} },
            () => { try { unsubscribeSolicitudes(); } catch (e) {} },
            () => { try { unsubscribeFinancialAlerts(); } catch (e) {} },
        ];

        const needsResident = Boolean(isResident && fraccionamientoId && uid);
        const needsAdmin = Boolean(isAdminRole && fraccionamientoId && uid);

        if (!needsResident) {
            setPendingVotes(0);
            setUnreadNotices(0);
            setPendingDeliveries(0);
            setRecentDeliveries([]);
            setResidentPendingReports(0);
        }
        if (!needsAdmin) {
            setPendingReports(0);
            setBallotsWithVotes(0);
            setTotalVotesCast(0);
            setPendingSolicitudes(0);
            setPendingSolicitudSummary({ mudanza: 0, construccion: 0, remodelacion: 0, otros: 0 });
        }

        const totalTargets = (needsResident ? 4 : 0) + (needsAdmin ? 3 : 0);
        if (totalTargets === 0) {
            setLoading(false);
            return () => undefined;
        }

        setLoading(true);
        let readyCount = 0;
        const markReady = () => {
            readyCount += 1;
            if (readyCount >= totalTargets && !cancelled) {
                setLoading(false);
            }
        };
        const makeReadyOnce = () => {
            let done = false;
            return () => {
                if (!done) {
                    done = true;
                    markReady();
                }
            };
        };

        if (needsResident) {
            let votacionesVersion = 0;
            let avisosVersion = 0;
            let entregasVersion = 0;
            let residentReportsVersion = 0;
            const residentVotesReady = makeReadyOnce();
            const residentNoticesReady = makeReadyOnce();
            const residentDeliveriesReady = makeReadyOnce();
            const residentReportsReady = makeReadyOnce();

            const votacionesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'votaciones');
            unsubscribeResidentVotes = onSnapshot(
                query(votacionesRef, orderBy('fechaCreacion', 'desc'), limit(25)),
                (snapshot) => {
                    const requestId = ++votacionesVersion;
                    (async () => {
                        try {
                            const activos = snapshot.docs.filter((docSnap) => {
                                const data = docSnap.data() || {};
                                const estado = computeEstadoVotacion(data);
                                return estado !== 'vencida';
                            });

                            if (!activos.length) {
                                if (!cancelled && requestId === votacionesVersion) {
                                    setPendingVotes(0);
                                }
                                residentVotesReady();
                                return;
                            }

                            const results = await Promise.all(
                                activos.map((docSnap) => (
                                    getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', docSnap.id, 'votantes', uid))
                                ))
                            );

                            if (!cancelled && requestId === votacionesVersion) {
                                const pendientes = results.reduce((acc, voteSnap) => (!voteSnap.exists() ? acc + 1 : acc), 0);
                                setPendingVotes(pendientes);
                            }
                        } catch (error) {
                            console.error('ResidentPendingProvider: error al obtener votaciones pendientes', error);
                            if (!cancelled && requestId === votacionesVersion) {
                                setPendingVotes(0);
                            }
                        } finally {
                            residentVotesReady();
                        }
                    })();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando votaciones', error);
                    if (!cancelled) {
                        setPendingVotes(0);
                    }
                    residentVotesReady();
                }
            );

            const avisosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'comunicados');
            unsubscribeResidentNotices = onSnapshot(
                query(avisosRef, orderBy('fecha', 'desc'), limit(40)),
                (snapshot) => {
                    const requestId = ++avisosVersion;
                    (async () => {
                        try {
                            if (!snapshot.size) {
                                if (!cancelled && requestId === avisosVersion) {
                                    setUnreadNotices(0);
                                }
                                residentNoticesReady();
                                return;
                            }

                            const entries = await Promise.all(
                                snapshot.docs.map((docSnap) => (
                                    getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', docSnap.id, 'lecturas', uid))
                                ))
                            );

                            if (!cancelled && requestId === avisosVersion) {
                                const unread = entries.reduce((acc, lecturaSnap) => (!lecturaSnap.exists() ? acc + 1 : acc), 0);
                                setUnreadNotices(unread);
                            }
                        } catch (error) {
                            console.error('ResidentPendingProvider: error al obtener avisos pendientes', error);
                            if (!cancelled && requestId === avisosVersion) {
                                setUnreadNotices(0);
                            }
                        } finally {
                            residentNoticesReady();
                        }
                    })();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando avisos', error);
                    if (!cancelled) {
                        setUnreadNotices(0);
                    }
                    residentNoticesReady();
                }
            );

            const notificacionesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones');
            unsubscribeResidentDeliveries = onSnapshot(
                query(
                    notificacionesRef,
                    where('destinatarioUid', '==', uid),
                    where('tipoMovimiento', '==', 'paqueteria_registrada'),
                    orderBy('fecha', 'desc'),
                    limit(20)
                ),
                (snapshot) => {
                    const requestId = ++entregasVersion;
                    try {
                        if (cancelled || requestId !== entregasVersion) {
                            return;
                        }

                        if (!snapshot.size) {
                            setPendingDeliveries(0);
                            setRecentDeliveries([]);
                            return;
                        }

                        const items = snapshot.docs.map((docSnap) => {
                            const data = docSnap.data() || {};
                            const fecha = typeof data.fecha?.toDate === 'function' ? data.fecha.toDate() : null;
                            return {
                                key: docSnap.id,
                                titulo: data.titulo || 'Paquetería registrada',
                                descripcion: data.descripcion || '',
                                fecha,
                                departamento: data.departamento || null,
                                empresa: data.empresa || null,
                                guardia: data.guardia || null,
                            };
                        });

                        items.sort((a, b) => {
                            const timeA = a.fecha ? a.fecha.getTime() : 0;
                            const timeB = b.fecha ? b.fecha.getTime() : 0;
                            return timeB - timeA;
                        });

                        setPendingDeliveries(items.length);
                        setRecentDeliveries(items.slice(0, 3));
                    } catch (error) {
                        console.error('ResidentPendingProvider: error al procesar notificaciones de paquetería', error);
                        if (!cancelled && requestId === entregasVersion) {
                            setPendingDeliveries(0);
                            setRecentDeliveries([]);
                        }
                    } finally {
                        if (!cancelled && requestId === entregasVersion) {
                            residentDeliveriesReady();
                        }
                    }
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando paquetería registrada', error);
                    if (!cancelled) {
                        setPendingDeliveries(0);
                        setRecentDeliveries([]);
                    }
                    residentDeliveriesReady();
                }
            );

            const residentReportsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias');
            unsubscribeResidentReports = onSnapshot(
                query(residentReportsRef, orderBy('fechaCreacion', 'desc')),
                (snapshot) => {
                    const requestId = ++residentReportsVersion;
                    (async () => {
                        try {
                            if (!snapshot.size) {
                                if (!cancelled && requestId === residentReportsVersion) {
                                    setResidentPendingReports(0);
                                }
                                residentReportsReady();
                                return;
                            }

                            const lecturaSnaps = await Promise.all(
                                snapshot.docs.map((docSnap) => (
                                    getDoc(doc(
                                        db,
                                        'fraccionamientos',
                                        fraccionamientoId,
                                        'propuestasIncidencias',
                                        docSnap.id,
                                        'lecturas',
                                        uid,
                                    ))
                                ))
                            );

                            if (cancelled || requestId !== residentReportsVersion) {
                                return;
                            }

                            const pendientes = snapshot.docs.reduce((acc, docSnap, index) => {
                                const data = docSnap.data() || {};
                                if (isReportResolved(data)) {
                                    return acc;
                                }

                                const creadorUid = getReportCreatorUid(data);
                                if (creadorUid && uid && creadorUid === uid) {
                                    return acc;
                                }

                                const lecturaSnap = lecturaSnaps[index];
                                return !lecturaSnap?.exists() ? acc + 1 : acc;
                            }, 0);

                            setResidentPendingReports(pendientes);
                        } catch (error) {
                            console.error('ResidentPendingProvider: error al calcular reportes pendientes (residentes)', error);
                            if (!cancelled && requestId === residentReportsVersion) {
                                setResidentPendingReports(0);
                            }
                        } finally {
                            if (!cancelled && requestId === residentReportsVersion) {
                                residentReportsReady();
                            }
                        }
                    })();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando reportes para residentes', error);
                    if (!cancelled) {
                        setResidentPendingReports(0);
                    }
                    residentReportsReady();
                }
            );

            // Listener para notificaciones de corte y sanciones financieras
            let financialAlertsVersion = 0;
            const financialAlertsReady = makeReadyOnce();

            const financialNotificationsRef = collection(db, 'usuarios', uid, 'notificaciones');
            unsubscribeFinancialAlerts = onSnapshot(
                query(
                    financialNotificationsRef,
                    where('tipo', 'in', ['advertencia_corte', 'corte_aplicado', 'sanciones_aplicadas', 'cambio_configuracion_financiera', 'multa_administrativa']),
                    where('leida', '==', false)
                    // Removido orderBy para evitar necesidad de índice compuesto
                    // limit(10) también removido, aplicaremos límite después de ordenar
                ),
                (snapshot) => {
                    const requestId = ++financialAlertsVersion;
                    try {
                        if (cancelled || requestId !== financialAlertsVersion) {
                            return;
                        }

                        if (!snapshot.size) {
                            setPendingFinancialAlerts(0);
                            setFinancialAlerts([]);
                            financialAlertsReady();
                            return;
                        }

                        const alerts = snapshot.docs.map((docSnap) => {
                            const data = docSnap.data() || {};
                            const fecha = typeof data.fecha?.toDate === 'function' ? data.fecha.toDate() : null;
                            return {
                                id: docSnap.id,
                                tipo: data.tipo || 'info',
                                titulo: data.titulo || 'Notificación financiera',
                                descripcion: data.descripcion || '',
                                fecha,
                                leida: data.leida || false,
                            };
                        });

                        // Ordenar por fecha en el cliente (más reciente primero)
                        alerts.sort((a, b) => {
                            const timeA = a.fecha ? a.fecha.getTime() : 0;
                            const timeB = b.fecha ? b.fecha.getTime() : 0;
                            return timeB - timeA;
                        });

                        // Aplicar límite después de ordenar
                        const limitedAlerts = alerts.slice(0, 10);
                        setPendingFinancialAlerts(limitedAlerts.length);
                        setFinancialAlerts(limitedAlerts.slice(0, 5));
                    } catch (error) {
                        console.error('ResidentPendingProvider: error al procesar notificaciones financieras', error);
                        if (!cancelled && requestId === financialAlertsVersion) {
                            setPendingFinancialAlerts(0);
                            setFinancialAlerts([]);
                        }
                    } finally {
                        if (!cancelled && requestId === financialAlertsVersion) {
                            financialAlertsReady();
                        }
                    }
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando notificaciones financieras', error);
                    if (!cancelled) {
                        setPendingFinancialAlerts(0);
                        setFinancialAlerts([]);
                    }
                    financialAlertsReady();
                }
            );
        }

        if (needsAdmin) {
            const reportsReady = makeReadyOnce();
            const adminVotesReady = makeReadyOnce();
            const solicitudesReady = makeReadyOnce();

            const reportesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias');
            let reportsVersion = 0;
            unsubscribeReports = onSnapshot(
                query(reportesRef, orderBy('fechaCreacion', 'desc')),
                (snapshot) => {
                    const requestId = ++reportsVersion;
                    (async () => {
                        try {
                            if (!snapshot.size) {
                                if (!cancelled && requestId === reportsVersion) {
                                    setPendingReports(0);
                                }
                                return;
                            }

                            const lecturaSnaps = await Promise.all(
                                snapshot.docs.map((docSnap) => (
                                    getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias', docSnap.id, 'lecturas', uid))
                                ))
                            );

                            if (cancelled || requestId !== reportsVersion) {
                                return;
                            }

                            const pendientes = snapshot.docs.reduce((acc, docSnap, index) => {
                                const data = docSnap.data() || {};
                                if (isReportResolved(data)) {
                                    return acc;
                                }
                                const creadorUid = getReportCreatorUid(data);
                                if (creadorUid && uid && creadorUid === uid) {
                                    return acc;
                                }
                                const lecturaSnap = lecturaSnaps[index];
                                return !lecturaSnap?.exists() ? acc + 1 : acc;
                            }, 0);

                            setPendingReports(pendientes);
                        } catch (error) {
                            console.error('ResidentPendingProvider: error al obtener lecturas de propuestas/incidencias', error);
                            if (!cancelled && requestId === reportsVersion) {
                                setPendingReports(0);
                            }
                        } finally {
                            reportsReady();
                        }
                    })();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando propuestas e incidencias', error);
                    if (!cancelled) {
                        setPendingReports(0);
                    }
                    reportsReady();
                }
            );

            const votacionesAdminRef = collection(db, 'fraccionamientos', fraccionamientoId, 'votaciones');
            let adminVotesVersion = 0;
            unsubscribeAdminVotes = onSnapshot(
                query(votacionesAdminRef, orderBy('fechaCreacion', 'desc')),
                (snapshot) => {
                    const requestId = ++adminVotesVersion;

                    (async () => {
                        try {
                            if (!snapshot.size) {
                                if (!cancelled && requestId === adminVotesVersion) {
                                    setBallotsWithVotes(0);
                                    setTotalVotesCast(0);
                                }
                                adminVotesReady();
                                return;
                            }

                            const activeDocs = snapshot.docs.filter((docSnap) => {
                                const data = docSnap.data() || {};
                                const estado = computeEstadoVotacion(data);
                                return estado === 'activa';
                            });

                            if (!activeDocs.length) {
                                if (!cancelled && requestId === adminVotesVersion) {
                                    setBallotsWithVotes(0);
                                    setTotalVotesCast(0);
                                }
                                adminVotesReady();
                                return;
                            }

                            const lecturaSnaps = await Promise.all(
                                activeDocs.map((docSnap) => (
                                    getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', docSnap.id, 'lecturasAdmin', uid))
                                ))
                            );

                            if (cancelled || requestId !== adminVotesVersion) {
                                return;
                            }

                            let pendingBallots = 0;
                            let pendingVotes = 0;

                            activeDocs.forEach((docSnap, index) => {
                                const data = docSnap.data() || {};
                                const totalVotes = sumVotes(data.opciones);
                                if (totalVotes <= 0) {
                                    return;
                                }

                                const ackData = lecturaSnaps[index]?.data() || {};
                                const acknowledged = Number(ackData.conteoTotal) || 0;

                                if (totalVotes > acknowledged) {
                                    pendingBallots += 1;
                                    pendingVotes += totalVotes - acknowledged;
                                }
                            });

                            setBallotsWithVotes(pendingBallots);
                            setTotalVotesCast(pendingVotes);
                        } catch (error) {
                            console.error('ResidentPendingProvider: error al calcular votaciones pendientes para administración', error);
                            if (!cancelled && requestId === adminVotesVersion) {
                                setBallotsWithVotes(0);
                                setTotalVotesCast(0);
                            }
                        } finally {
                            if (!cancelled && requestId === adminVotesVersion) {
                                adminVotesReady();
                            }
                        }
                    })();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando votaciones para administración', error);
                    if (!cancelled) {
                        setBallotsWithVotes(0);
                        setTotalVotesCast(0);
                    }
                    adminVotesReady();
                }
            );

            const solicitudesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
            unsubscribeSolicitudes = onSnapshot(
                query(solicitudesRef, where('segmento', '==', 'solicitudes')),
                (snapshot) => {
                    if (!cancelled) {
                        const summary = { mudanza: 0, construccion: 0, remodelacion: 0, otros: 0 };
                        let pendientes = 0;

                        snapshot.docs.forEach((docSnap) => {
                            const data = docSnap.data() || {};
                            const estado = String(data.estado || 'pendiente').toLowerCase();
                            if (estado !== 'pendiente') {
                                return;
                            }
                            pendientes += 1;
                            const tipo = String(data.tipoSolicitud || '').toLowerCase();
                            if (tipo === 'mudanza') {
                                summary.mudanza += 1;
                            } else if (tipo === 'construccion' || tipo === 'permiso de construcción' || tipo === 'permiso de construccion') {
                                summary.construccion += 1;
                            } else if (tipo === 'remodelacion') {
                                summary.remodelacion += 1;
                            } else {
                                summary.otros += 1;
                            }
                        });

                        setPendingSolicitudes(pendientes);
                        setPendingSolicitudSummary(summary);
                    }
                    solicitudesReady();
                },
                (error) => {
                    console.error('ResidentPendingProvider: error escuchando solicitudes pendientes', error);
                    if (!cancelled) {
                        setPendingSolicitudes(0);
                        setPendingSolicitudSummary({ mudanza: 0, construccion: 0, remodelacion: 0, otros: 0 });
                    }
                    solicitudesReady();
                }
            );
        }

        return () => {
            cancelled = true;
            try { unsubscribeResidentVotes(); } catch (e) {}
            try { unsubscribeResidentNotices(); } catch (e) {}
            try { unsubscribeResidentDeliveries(); } catch (e) {}
            try { unsubscribeResidentReports(); } catch (e) {}
            try { unsubscribeReports(); } catch (e) {}
            try { unsubscribeAdminVotes(); } catch (e) {}
            try { unsubscribeSolicitudes(); } catch (e) {}
            try { unsubscribeFinancialAlerts(); } catch (e) {}
            activeUnsubscribesRef.current = [];
        };
    }, [isResident, isAdminRole, fraccionamientoId, uid]);

    const totalPending = useMemo(
        () => pendingVotes + unreadNotices + pendingDeliveries + residentPendingReports + pendingFinancialAlerts,
        [pendingVotes, unreadNotices, pendingDeliveries, residentPendingReports, pendingFinancialAlerts]
    );
    const adminAlertTotal = useMemo(() => {
        const votesReference = totalVotesCast > 0 ? totalVotesCast : ballotsWithVotes;
        return pendingReports + votesReference + pendingSolicitudes;
    }, [pendingReports, ballotsWithVotes, totalVotesCast, pendingSolicitudes]);

    const value = useMemo(() => ({
        pendingVotes,
        unreadNotices,
        pendingDeliveries,
        recentDeliveries,
        totalPending,
        pendingReports,
        residentPendingReports,
        ballotsWithVotes,
        totalVotesCast,
        pendingSolicitudes,
        pendingSolicitudSummary,
        adminAlertTotal,
        pendingFinancialAlerts,
        financialAlerts,
        loading,
        isResident,
        isAdminRole,
        role: normalizedRole,
    }), [
        pendingVotes,
        unreadNotices,
        pendingDeliveries,
        recentDeliveries,
        totalPending,
        pendingReports,
        residentPendingReports,
        ballotsWithVotes,
        totalVotesCast,
        pendingSolicitudes,
        pendingSolicitudSummary,
        adminAlertTotal,
        pendingFinancialAlerts,
        financialAlerts,
        loading,
        isResident,
        isAdminRole,
        normalizedRole,
    ]);

    return (
        <ResidentPendingContext.Provider value={value}>
            {children}
        </ResidentPendingContext.Provider>
    );
};

export const useResidentPending = () => useContext(ResidentPendingContext);

function computeEstadoVotacion(data = {}) {
    const ahora = new Date();
    const inicio = normalizarFecha(data.fechaCreacion);
    const fin = normalizarFecha(data.fechaFin);

    if (inicio && ahora < inicio) {
        return 'programada';
    }
    if (fin && ahora >= fin) {
        return 'vencida';
    }
    const estado = String(data.estado || '').toLowerCase();
    return estado || 'activa';
}

function getReportCreatorUid(data = {}) {
    const candidates = [
        data.creadorId,
        data.creadorUid,
        data.uid,
        data.usuarioId,
        data.userId,
        data.autorId,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate.trim();
        }
    }

    return null;
}

function normalizarFecha(valor) {
    if (!valor) return null;
    if (typeof valor?.toDate === 'function') {
        return valor.toDate();
    }
    if (valor instanceof Date) {
        return valor;
    }
    const parsed = new Date(valor);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function sumVotes(opciones = {}) {
    return Object.values(opciones || {}).reduce((acc, value) => acc + (Number(value) || 0), 0);
}

function isReportResolved(data = {}) {
    const estado = String(data.estado || '').toLowerCase().trim();
    if (!estado) {
        return false;
    }
    const normalized = estado.replace(/\s+/g, '-');
    return RESOLVED_REPORT_STATUSES.has(normalized);
}
